// notifyChadOnDrawRequest — fires when an RM submits a new draw request
// from MyPipeline → Commissions tab. Mirrors notifyChadOnBuildApproval pattern:
// in-portal notification on /finance?view=chad&tab=draws + Twilio SMS to Chad's
// cell via the Bardin Team line. Fire-and-forget.
//
// Spec: memory/features/finance-board-redesign.md (Ship 6, §5)

import { supabase } from "./supabase";

const PORTAL_BASE_URL = "https://app.bardinroofing.com";
const CHAD_EMAIL = "chad@bardinroofing.com";

function toE164(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

export async function notifyChadOnDrawRequest(opts: {
  draw_id: string;
  requester_name: string;     // "Alex Poma" or "Risol Construction"
  requester_role: string;     // "ASM" / "RM" / "TL" / "Roof Crew"
  amount: number;
  reason: string | null;
}): Promise<void> {
  try {
    const { data: chad, error: lookupErr } = await supabase
      .from("team_roster")
      .select("id,full_name,phone")
      .eq("email", CHAD_EMAIL)
      .eq("is_active", true)
      .maybeSingle();

    if (lookupErr) {
      console.warn("[notifyChadOnDrawRequest] team_roster lookup failed:", lookupErr.message);
      return;
    }
    if (!chad) {
      console.warn("[notifyChadOnDrawRequest] Chad row not found / inactive — skipping text.");
      return;
    }

    const drawUrl = `${PORTAL_BASE_URL}/finance?view=chad&tab=draws`;
    const dollar = "$" + (opts.amount).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    const reasonShort = opts.reason ? ` — "${opts.reason.slice(0, 60)}${opts.reason.length > 60 ? "…" : ""}"` : "";
    const smsBody = `[Bardin Team] New draw request: ${opts.requester_name} (${opts.requester_role}) ${dollar}${reasonShort}. Tap: ${drawUrl}`;

    // 1) In-app notification — appears on /notifications
    const inAppPromise = supabase.from("notifications").insert({
      user_id: chad.id,
      type: "draw_request",
      title: `Draw request — ${opts.requester_name} ${dollar}`,
      body: opts.reason || `${opts.requester_role} requested ${dollar}`,
      metadata: { url: drawUrl, draw_id: opts.draw_id, source: "rm_pipeline_request" },
    });

    // 2) Bardin Team SMS (no deal_id passed → no homeowner mirror)
    const phoneE164 = toE164(chad.phone);
    let smsPromise: Promise<unknown> = Promise.resolve();
    if (phoneE164) {
      smsPromise = supabase.functions.invoke("twilio-send-sms", {
        body: { to: phoneE164, body: smsBody, tag: "draw_request" },
      });
    } else {
      console.warn("[notifyChadOnDrawRequest] Chad has no phone on team_roster — skipping SMS.");
    }

    const [inAppRes, smsRes] = await Promise.allSettled([inAppPromise, smsPromise]);
    if (inAppRes.status === "rejected") {
      console.warn("[notifyChadOnDrawRequest] in-app insert failed:", inAppRes.reason);
    }
    if (smsRes.status === "rejected") {
      console.warn("[notifyChadOnDrawRequest] SMS send failed:", smsRes.reason);
    }
  } catch (err) {
    console.warn("[notifyChadOnDrawRequest] unexpected error:", err);
  }
}

/**
 * Fired when Chad approves or rejects a draw — the requester gets a notification
 * + SMS so they can act (or know to expect / not expect Shannon's disbursement).
 */
export async function notifyOnDrawDecision(opts: {
  draw_id: string;
  recipient_team_member_id: string | null;
  recipient_kind: "team_member" | "vendor";
  decision: "approved" | "rejected";
  approved_amount: number | null;
  reason: string | null;
}): Promise<void> {
  try {
    if (opts.recipient_kind !== "team_member" || !opts.recipient_team_member_id) {
      // Vendor draws: Chad enters them himself, no requester notification needed.
      return;
    }
    const { data: member, error } = await supabase
      .from("team_roster")
      .select("id,full_name,phone")
      .eq("id", opts.recipient_team_member_id)
      .maybeSingle();
    if (error || !member) return;

    const dollar = opts.approved_amount != null
      ? "$" + opts.approved_amount.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })
      : "—";
    const verb = opts.decision === "approved" ? "approved" : "rejected";
    const verbCap = verb[0].toUpperCase() + verb.slice(1);
    const noteSuffix = opts.reason ? ` — "${opts.reason.slice(0, 80)}"` : "";
    const dealUrl = `${PORTAL_BASE_URL}/pipeline?tab=commissions`;

    const smsBody = opts.decision === "approved"
      ? `[Bardin Team] Draw ${verb}: ${dollar}. Shannon will disburse next pay cycle.${noteSuffix}`
      : `[Bardin Team] Draw ${verb}.${noteSuffix} Tap to discuss: ${dealUrl}`;

    await Promise.allSettled([
      supabase.from("notifications").insert({
        user_id: member.id,
        type: `draw_${verb}`,
        title: `Draw ${verbCap} — ${dollar}`,
        body: opts.reason || `${verbCap} by Chad`,
        metadata: { url: dealUrl, draw_id: opts.draw_id },
      }),
      (async () => {
        const phone = toE164(member.phone);
        if (!phone) return;
        return supabase.functions.invoke("twilio-send-sms", {
          body: { to: phone, body: smsBody, tag: `draw_${verb}` },
        });
      })(),
    ]);
  } catch (err) {
    console.warn("[notifyOnDrawDecision] unexpected error:", err);
  }
}
